/**
 * La aritmética que comparten la pantalla y el PDF.
 *
 * Vive aparte porque es lo único de todo esto que se puede probar sin montar un
 * componente ni abrir un PDF: si el aro de «3 h 30 de 24 h» mide distinto en la
 * pantalla que en el archivo, el bug está aquí y una prueba lo caza.
 */

import type { Tono, TramoRegla } from "./tipos";

/**
 * Los colores del documento.
 *
 * ── Por qué están en hexadecimal y no en tokens del tema ────────────────────
 * El PDF no tiene CSS: jsPDF quiere números. Y el documento NO conmuta con el
 * tema: un reporte que se imprime en oscuro gasta un cartucho y se lee peor. La
 * hoja es blanca en los dos temas, igual que el papel.
 *
 * La pantalla los usa tal cual para que la previsualización sea el documento, no
 * una aproximación del documento con los colores de la app.
 */
export const TINTA = {
  navy: "#0F2A47",
  azul: "#1565C0",
  azulClaro: "#E8F0FE",
  cian: "#22D3EE",
  verde: "#1D9E75",
  ambar: "#EF9F27",
  rojo: "#E24B4A",
  gris: "#6B7280",
  tenue: "#9AA5B1",
  linea: "#E5E7EB",
  lineaSuave: "#F0F3F7",
  cebra: "#FAFBFD",
  texto: "#1F2937",
} as const;

export const TONO_TINTA: Record<Tono, string> = {
  neutral: TINTA.tenue,
  info: TINTA.azul,
  exito: TINTA.verde,
  aviso: TINTA.ambar,
  error: TINTA.rojo,
};

/**
 * La paleta del PDF de facturación (tema `wayak`, modo claro). La usan los
 * reportes con `estilo: "factura"` para verse como ese comprobante.
 */
export const TINTA_FACTURA = {
  navy: "#19273a",
  primario: "#0099f5",
  texto: "#00001b",
  textoSecundario: "#4c648b",
  tenue: "#6b81a5",
  tarjeta: "#f1f5fc",
  borde: "#d3dff3",
  bordeSuave: "#e3ecf9",
  exito: "#16a34a",
  aviso: "#d08501",
  error: "#dc2626",
  subtituloBanda: "#c6d3e6",
} as const;

export const TONO_FACTURA: Record<Tono, string> = {
  neutral: TINTA_FACTURA.tenue,
  info: TINTA_FACTURA.primario,
  exito: TINTA_FACTURA.exito,
  aviso: TINTA_FACTURA.aviso,
  error: TINTA_FACTURA.error,
};

/** `#1565C0` → `[21, 101, 192]`, que es lo que jsPDF acepta. */
export function aRgb(hex: string): [number, number, number] {
  const limpio = hex.replace("#", "");
  const n = parseInt(
    limpio.length === 3
      ? limpio.split("").map((c) => c + c).join("")
      : limpio,
    16,
  );
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export const DIA_MS = 24 * 60 * 60 * 1000;

/**
 * El arco de un reloj, en la forma que quieren los dos renderizadores.
 *
 * Devuelve la longitud del trazo y la de la vuelta completa, que es como se
 * dibuja un donut con `stroke-dasharray` en SVG y con arcos en el PDF.
 *
 * Los tres estados son deliberados y NO se pueden colapsar:
 *   - `parcial`: el indicador es parte de algo y el arco dice cuánta parte.
 *   - `lleno`: absoluto sin denominador. El aro cerrado afirma «completo».
 *   - `vacio`: no se sabe. Se dibuja punteado, que no es lo mismo que cero.
 */
export function arco(
  fraccion: number | null,
  valor: string | null,
  radio: number,
): { avance: number; vuelta: number; modo: "parcial" | "lleno" | "vacio" } {
  const vuelta = 2 * Math.PI * radio;
  if (valor === null || valor === undefined || valor === "") {
    return { avance: 0, vuelta, modo: "vacio" };
  }
  if (fraccion === null || !Number.isFinite(fraccion)) {
    return { avance: vuelta, vuelta, modo: "lleno" };
  }
  const f = Math.max(0, Math.min(1, fraccion));
  return { avance: f * vuelta, vuelta, modo: "parcial" };
}

export interface BarraRegla {
  /** Porcentaje del ancho total donde empieza la barra. */
  izquierda: number;
  /** Porcentaje del ancho total que ocupa. */
  ancho: number;
  estado: string;
}

/**
 * Los tramos de un día recortados a su ventana de 24 h y pasados a porcentaje.
 *
 * El recorte importa: un tramo que empieza antes de la medianoche, pintado
 * entero, correría todo lo demás hacia la derecha y la regla dejaría de
 * coincidir con las horas de abajo.
 *
 * `MINIMO_VISIBLE` existe porque un hueco de dos minutos es 0.14 % del día y no
 * se vería — pero un hueco de dos minutos a media jornada es justo lo que
 * alguien está buscando cuando mira esta regla.
 */
const MINIMO_VISIBLE = 0.35;

export function barrasDelDia(tramos: TramoRegla[], inicio: number): BarraRegla[] {
  const out: BarraRegla[] = [];
  for (const t of tramos) {
    const desde = Math.max(t.desde, inicio);
    const hasta = Math.min(t.hasta, inicio + DIA_MS);
    if (hasta <= desde) continue;
    out.push({
      izquierda: ((desde - inicio) / DIA_MS) * 100,
      ancho: Math.max(((hasta - desde) / DIA_MS) * 100, MINIMO_VISIBLE),
      estado: t.estado,
    });
  }
  return out;
}

/** Los colores de la regla. Mismos nombres que usa Centinela para sus estados. */
export const TINTA_ESTADO: Record<string, string> = {
  conduciendo: TINTA.verde,
  trafico: TINTA.ambar,
  ralenti: TINTA.azul,
  detenido: "#C7CFD9",
  sin_datos: "#F3B9B8",
};

export function tintaDeEstado(estado: string): string {
  return TINTA_ESTADO[estado] ?? TINTA.tenue;
}
